import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  background: rgb(var(--bg));
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(255, 127, 80, 0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #ff7f50;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #ff7f50;
  border-bottom: 1px solid rgba(255, 127, 80, 0.3);
  padding-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #ff7f50;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(255, 127, 80, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(255, 127, 80, 0.5);
  }
`;

export const HowTheWorldReallyWorksMetadata = {
  title: "How the World Really Works",
  description: "A guide to understanding the world's systems and structures by Vaclav Smil.",
  tags: ['Science', 'Energy', 'Environment', 'Sustainability', 'Innovation'],
  author: "Vaclav Smil",
  datePublished: "2024-01-24",
  category: "Book Review",
  path: "/book-review/how-the-world-really-works",
  coverImage: "/images/how_the_world_really_works.jpg", // Ensure this path is correct
};

const HowTheWorldReallyWorks = () => {
  const navigate = useNavigate();
  const metadata = HowTheWorldReallyWorksMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
          {metadata.tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
      </TagContainer>
      <br />
      <br />
      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            In "How the World Really Works", Vaclav Smil delves into the mechanisms and structures that shape our modern world.
            From energy and technology to food and economy, Smil provides insights into the critical processes that sustain society.
          </p>
          <br />
          <p>
            The book offers an in-depth look at the complexities of global systems and challenges commonly held beliefs about progress
            and sustainability. Smil's scientific approach is both enlightening and provocative, urging readers to question their
            assumptions and gain a deeper understanding of the forces that drive our world.
          </p>
          <br />
          <p>
            With a focus on factual evidence and historical perspective, Smil discusses the implications of energy consumption,
            technological advancements, and the environmental impact of human activities. The book serves as a guide for those
            looking to comprehend the past, present, and future of our planet.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>Energy Systems:</strong> An exploration of global energy consumption and its impact on economies and environments.</ListItem>
            <ListItem><strong>Technological Advances:</strong> How technology shapes society and influences the global economy.</ListItem>
            <ListItem><strong>Global Processes:</strong> Insights into the interconnected systems that sustain modern civilization.</ListItem>
            <ListItem><strong>Environmental Impact:</strong> The consequences of human activity on the planet's ecosystems and climate.</ListItem>
            <ListItem><strong>Sustainability and Policy:</strong> A look into the future and the measures needed for sustainable development.</ListItem>
            <ListItem><strong>Scientific Perspective:</strong> The importance of understanding scientific evidence in shaping informed decisions.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "Understanding the world’s complexity is the first step in learning to navigate its challenges."
          </Quote>
          <Quote>
            "Energy is the foundation of modern civilization, and our dependence on it has shaped the world as we know it."
          </Quote>
        </SectionContent>
      </ContentSection>

      {/* Personal Reflection Section */}
      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            Reading "How the World Really Works" a few months after finishing <a href="/book-review/the-sixth-extinction">"The Sixth Extinction"</a> was a natural progression 
            in understanding the complex relationship between human activity and the environment. Where "The Sixth Extinction" highlighted the dire consequences of 
            biodiversity loss and environmental degradation, Smil's book dives deeper into the underlying systems—like energy consumption and technology—that 
            have led us to this point.
          </p>
          <br />
          <p>
            One of the major takeaways for me was how closely energy and environmental impact are intertwined. Smil provides a sobering reminder that while we 
            focus on the need for sustainability, our dependence on fossil fuels and energy-intensive technologies makes progress much more difficult than it appears.
            This built on the ideas I encountered in *The Sixth Extinction*, where the human footprint on ecosystems is a recurring theme.
          </p>
          <br />
          <p>
            The most eye-opening aspect of Smil’s work is his focus on data and evidence. He systematically breaks down myths about progress, revealing the challenges 
            of transforming our energy systems and the scale of what is required for true sustainability. It gave me a much clearer understanding of the complexity 
            of the problem, especially when thinking about how to balance technological growth with environmental protection.
          </p>
          <br />
          <p>
            Both books have deepened my appreciation for the intricate systems that sustain life and how interconnected they all are. Smil’s scientific approach, 
            combined with the stark warnings in "The Sixth Extinction", has left me with a renewed sense of urgency to support more sustainable practices in my own life 
            and to advocate for policies that prioritize long-term environmental health.
          </p>
        </SectionContent>
      </ContentSection>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default HowTheWorldReallyWorks;